import { AuthenticationScheme } from '@see-power-pft-common-js/auth/enums';
import { AuthStoreAccess } from 'common-react/auth/auth-store';
import { AuthActionHooks, IAuthScheme } from 'common-react/auth/AuthProvider';
import { getDevSchemeAuthenticationJwtToken } from 'common-react/auth/schemes/dev/dev';
import { useCallback, useMemo } from 'react';

const commonLogin = async (props: {
  authStoreAccess: AuthStoreAccess;
  actionHooks: AuthActionHooks;
}) => {
  props.actionHooks.onStartLogin();

  props.authStoreAccess.resetForAuthenticationScheme(AuthenticationScheme.DEV);

  const newJwtToken = await getDevSchemeAuthenticationJwtToken();

  if (newJwtToken) {
    props.authStoreAccess.setJwtToken(newJwtToken);

    props.actionHooks.onEndLogin();

    return;
  }

  props.actionHooks.onStartLogout();

  props.authStoreAccess.clearAuthState();

  props.actionHooks.onEndLogout();
};

export const useDevAuthScheme = (props: {
  authStoreAccess: AuthStoreAccess;
  actionHooks: AuthActionHooks;
}): IAuthScheme => {
  const isEnabled = useMemo(
    () =>
      import.meta.env.VITE_DEV_MODE === 'true' &&
      !!import.meta.env.VITE_DEV_EMAIL,
    [],
  );

  const logout = useCallback(async () => {
    if (
      props.authStoreAccess.getAuthenticationScheme() !==
      AuthenticationScheme.DEV
    ) {
      return;
    }

    props.actionHooks.onStartLogout();

    props.authStoreAccess.clearAuthState();

    props.actionHooks.onEndLogout();
  }, [props]);

  const login = useCallback(async () => {
    if (props.authStoreAccess.getAuthenticationScheme()) {
      return;
    }

    await commonLogin(props);
  }, [props]);

  const refresh = useCallback(async () => {
    await commonLogin(props);
  }, [props]);

  return useMemo(
    () => ({ isEnabled, login, logout, refresh }),
    [isEnabled, login, logout, refresh],
  );
};
